import { api } from '../../api';
import type { User, UserRole } from '@repo/types';
import { roleInfo } from '@repo/types';

// DTO for creating a user with roles (matches backend CreateUserWithRolesDto)
export interface CreateUserPayload {
  name: string;
  email: string;
  phone: string;
  password?: string;
  roles?: {
    role: UserRole;
    branchId?: string;
    branchIds?: string[];
    trackIds?: string[];
    isTeamLead?: boolean;
  }[];
}

// DTO for updating a user (matches backend UpdateUserDto)
export interface UpdateUserPayload {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  dateOfBirth?: string;
  address?: string;
  nationalId?: string;
  nationalIdImage?: string;
  profileImage?: string;
}

// Paginated response from backend
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Query params for user list
export interface UserQueryParams {
  page?: number;
  limit?: number;
  role?: UserRole;
  search?: string;
  status?: string;
}

export interface DashboardStats {
  generatedAt: string;
  overview: {
    totalUsers: number;
    activeRoleAssignments: number;
    activeGroups: number;
    totalEnrollments: number;
    monthlyCollectedRevenue: number;
    newLeadsThisMonth: number;
    newUsersThisMonth: number;
    submissionsThisMonth: number;
    leadConversionRate: number;
    collectionRate: number;
  };
  monthlyActivity: Array<{
    month: string;
    label: string;
    users: number;
    orders: number;
    leads: number;
    submissions: number;
  }>;
  users: {
    total: number;
    newThisMonth: number;
    roles: Record<string, number>;
    activeRoles: number;
    suspendedRoles: number;
    roleBreakdown: Array<{
      key: string;
      count: number;
    }>;
  };
  academic: {
    tracks: number;
    programs: number;
    bundles: number;
    categories: number;
    cycles: number;
  };
  operations: {
    branches: {
      total: number;
      active: number;
      physical: number;
    };
    groups: {
      total: number;
      active: number;
      inactive: number;
      archived: number;
      upcoming: number;
      totalSeats: number;
      occupiedSeats: number;
      seatUtilizationRate: number;
    };
    sessions: {
      total: number;
      completed: number;
      pending: number;
      today: number;
      upcoming: number;
    };
    enrollments: {
      total: number;
      active: number;
      waitingList: number;
      newThisMonth: number;
    };
  };
  finance: {
    orders: number;
    vouchers: number;
    grossRevenue: number;
    netRevenue: number;
    collectedRevenue: number;
    outstandingRevenue: number;
    collectionRate: number;
    orderStatusBreakdown: Array<{
      key: string;
      count: number;
    }>;
  };
  sales: {
    leads: number;
    convertedLeads: number;
    conversionRate: number;
    leadSources: Array<{
      key: string;
      count: number;
    }>;
    forms: {
      total: number;
      active: number;
      closed: number;
      acceptingResponses: number;
      topPerforming: Array<{
        _id: string;
        title: string;
        submissions: number;
      }>;
    };
    submissions: {
      total: number;
      assigned: number;
      finished: number;
      unassigned: number;
      statusBreakdown: Array<{
        key: string;
        count: number;
      }>;
    };
  };
  labels: number;
}

export const UserService = {
  getAll: async (
    params?: UserQueryParams
  ): Promise<{ data: User[]; pagination: PaginatedResponse<User>['pagination'] }> => {
    const response = await api.get<PaginatedResponse<User>>('/super-admin/users', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getById: async (id: string): Promise<{ data: User }> => {
    const response = await api.get<User>(`/super-admin/users/${id}`);
    return { data: response.data };
  },

  create: async (data: CreateUserPayload): Promise<{ data: { message: string } }> => {
    const response = await api.post<{ message: string }>('/super-admin/users', data);
    return { data: response.data };
  },

  getInstructors: async (params?: {
    trackId?: string;
    search?: string;
    limit?: number;
  }): Promise<{ data: any[] }> => {
    const response = await api.get<{ success: boolean; data: any[] }>('/shared/instructors', {
      params,
    });
    return { data: response.data.data };
  },

  getStudents: async (params?: { search?: string }): Promise<{ data: any[] }> => {
    const response = await api.get<{ success: boolean; data: any[] }>('/shared/students', {
      params,
    });
    return { data: response.data.data };
  },

  getMentors: async (params?: {
    trackId?: string;
    search?: string;
    limit?: number;
  }): Promise<{ data: any[] }> => {
    const response = await api.get<{ success: boolean; data: any[] }>('/shared/mentors', {
      params,
    });
    return { data: response.data.data };
  },

  update: async (
    id: string,
    data: UpdateUserPayload
  ): Promise<{ data: { message: string; data: User } }> => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${id}`,
      data
    );
    return { data: response.data };
  },

  // Assign a role to existing user
  assignRole: async (
    userId: string,
    roleData: {
      role: UserRole;
      branchId?: string;
      branchIds?: string[];
      trackIds?: string[];
      isTeamLead?: boolean;
    }
  ): Promise<{ data: { message: string } }> => {
    const response = await api.post<{ message: string }>(
      `/super-admin/users/${userId}/roles`,
      roleData
    );
    return { data: response.data };
  },

  // Remove a role from user (hard delete)
  removeRole: async (userId: string, role: UserRole): Promise<{ data: { message: string } }> => {
    const response = await api.delete<{ message: string }>(
      `/super-admin/users/${userId}/roles/${role}`
    );
    return { data: response.data };
  },

  // Suspend a role for a user (soft delete)
  suspendRole: async (userId: string, role: UserRole): Promise<{ data: { message: string } }> => {
    const response = await api.patch<{ message: string }>(`/super-admin/users/${userId}/suspend`, {
      role,
    });
    return { data: response.data };
  },

  // Activate a role for a user (restore)
  activateRole: async (userId: string, role: UserRole): Promise<{ data: { message: string } }> => {
    const response = await api.patch<{ message: string }>(`/super-admin/users/${userId}/activate`, {
      role,
    });
    return { data: response.data };
  },

  // Get available roles
  getRoles: (): UserRole[] => {
    return Object.keys(roleInfo) as UserRole[];
  },

  // Get role display name
  getRoleDisplayName: (role: UserRole): string => {
    return roleInfo[role]?.label || role;
  },

  // Get role description
  getRoleDescription: (role: UserRole): string => {
    return roleInfo[role]?.description || '';
  },

  // Check if role requires branch assignment
  roleRequiresBranch: (role: UserRole): boolean => {
    return role === 'OperationManager' || role === 'Sales';
  },

  // Check if role requires track assignment
  roleRequiresTrack: (role: UserRole): boolean => {
    return role === 'Mentor' || role === 'Instructor';
  },

  // Change password
  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ message: string }> => {
    const response = await api.patch('/auth/change-password', data);
    return response.data;
  },

  updateProfileImage: async (
    formData: FormData
  ): Promise<{ message?: string; profileImage?: string; user?: any }> => {
    const response = await api.patch('/auth/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Admin Reset Password
  resetPassword: async (
    userId: string
  ): Promise<{ data: { message: string; newPassword?: string } }> => {
    const response = await api.patch<{ message: string; newPassword?: string }>(
      `/super-admin/users/${userId}/reset-password`
    );
    return { data: response.data };
  },

  // Reset device binding (when student changes phone)
  resetDeviceBinding: async (userId: string): Promise<{ data: { message: string } }> => {
    const response = await api.patch<{ message: string }>(
      `/super-admin/users/${userId}/reset-device`
    );
    return { data: response.data };
  },

  // Set Team Lead Status
  setTeamLead: async (
    userId: string,
    data: { role: UserRole; isTeamLead: boolean }
  ): Promise<{ data: { message: string; data: User } }> => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${userId}/roles/team-lead`,
      data
    );
    return { data: response.data };
  },

  // Set Salary Eligibility Status
  setSalaryEligibility: async (
    userId: string,
    data: { role: UserRole; isSalaryEligible: boolean }
  ): Promise<{ data: { message: string; data: User } }> => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${userId}/roles/salary-eligibility`,
      data
    );
    return { data: response.data };
  },

  // Role specific updates
  updateSalesInfo: async (id: string, data: any) => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${id}/roles/sales`,
      data
    );
    return { data: response.data };
  },

  updateOperationManagerInfo: async (id: string, data: any) => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${id}/roles/operation-manager`,
      data
    );
    return { data: response.data };
  },

  updateMentorInfo: async (id: string, data: any) => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${id}/roles/mentor`,
      data
    );
    return { data: response.data };
  },

  updateInstructorInfo: async (id: string, data: any) => {
    const response = await api.patch<{ message: string; data: User }>(
      `/super-admin/users/${id}/roles/instructor`,
      data
    );
    return { data: response.data };
  },

  getDashboardStats: async () => {
    const response = await api.get<DashboardStats>('/super-admin/dashboard/stats');
    return response.data;
  },
};

// Roles that admin is allowed to manage
export const ADMIN_ALLOWED_ROLES: UserRole[] = ['Instructor', 'Mentor'];

/**
 * AdminUserService - Limited user management for admin portal
 * Calls /admin/users/* endpoints (Instructor/Mentor only)
 */
export const AdminUserService = {
  getAll: async (
    params?: UserQueryParams
  ): Promise<{ data: User[]; pagination: PaginatedResponse<User>['pagination'] }> => {
    const response = await api.get<PaginatedResponse<User>>('/admin/users', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getById: async (id: string): Promise<{ data: User }> => {
    const response = await api.get<User>(`/admin/users/${id}`);
    return { data: response.data };
  },

  create: async (data: CreateUserPayload): Promise<{ data: { message: string } }> => {
    const response = await api.post<{ message: string }>('/admin/users', data);
    return { data: response.data };
  },

  update: async (
    id: string,
    data: UpdateUserPayload
  ): Promise<{ data: { message: string; data: User } }> => {
    const response = await api.patch<{ message: string; data: User }>(`/admin/users/${id}`, data);
    return { data: response.data };
  },

  assignRole: async (
    userId: string,
    roleData: {
      role: UserRole;
      branchId?: string;
      branchIds?: string[];
      trackIds?: string[];
      isTeamLead?: boolean;
    }
  ): Promise<{ data: { message: string } }> => {
    const response = await api.post<{ message: string }>(`/admin/users/${userId}/roles`, roleData);
    return { data: response.data };
  },

  suspendRole: async (userId: string, role: UserRole): Promise<{ data: { message: string } }> => {
    const response = await api.patch<{ message: string }>(`/admin/users/${userId}/suspend`, {
      role,
    });
    return { data: response.data };
  },

  activateRole: async (userId: string, role: UserRole): Promise<{ data: { message: string } }> => {
    const response = await api.patch<{ message: string }>(`/admin/users/${userId}/activate`, {
      role,
    });
    return { data: response.data };
  },

  resetPassword: async (
    userId: string
  ): Promise<{ data: { message: string; newPassword?: string } }> => {
    const response = await api.patch<{ message: string; newPassword?: string }>(
      `/admin/users/${userId}/reset-password`
    );
    return { data: response.data };
  },

  // Reset device binding (when student changes phone)
  resetDeviceBinding: async (userId: string): Promise<{ data: { message: string } }> => {
    const response = await api.patch<{ message: string }>(`/admin/users/${userId}/reset-device`);
    return { data: response.data };
  },

  updateMentorInfo: async (id: string, data: any) => {
    const response = await api.patch<{ message: string; data: User }>(
      `/admin/users/${id}/roles/mentor`,
      data
    );
    return { data: response.data };
  },

  updateInstructorInfo: async (id: string, data: any) => {
    const response = await api.patch<{ message: string; data: User }>(
      `/admin/users/${id}/roles/instructor`,
      data
    );
    return { data: response.data };
  },

  // Stubs for shared components — unreachable for Instructor/Mentor
  // but needed for TypeScript compilation since components are shared with super-admin
  setTeamLead: async (_userId: string, _data: any) => {
    throw new Error('Not available for admin');
  },
  removeRole: async (_userId: string, _role: any) => {
    throw new Error('Not available for admin');
  },
  updateSalesInfo: async (_id: string, _data: any) => {
    throw new Error('Not available for admin');
  },
  updateOperationManagerInfo: async (_id: string, _data: any) => {
    throw new Error('Not available for admin');
  },

  // Delegate to shared endpoints (no auth restriction)
  getInstructors: UserService.getInstructors,
  getMentors: UserService.getMentors,

  // Reuse helpers from UserService
  getRoles: (): UserRole[] => ADMIN_ALLOWED_ROLES,
  getRoleDisplayName: UserService.getRoleDisplayName,
  getRoleDescription: UserService.getRoleDescription,
  roleRequiresBranch: UserService.roleRequiresBranch,
  roleRequiresTrack: UserService.roleRequiresTrack,
};
